import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Building2, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const formatCurrency = (val) => new Intl.NumberFormat('en-US', { 
  style: 'currency', 
  currency: 'USD', 
  minimumFractionDigits: 2, 
  maximumFractionDigits: 2 
}).format(val || 0);

export default function SelectProjectForDrawDialog({ 
  isOpen, 
  onClose, 
  projects 
}) {
  const navigate = useNavigate();
  
  // Filter to only projects with funds available
  const projectsWithFunds = projects.filter(p => (p.loan_available_for_draw || 0) > 0);
  
  const handleSelectProject = (projectId) => {
    onClose();
    navigate(`/project/${projectId}?openDraw=true`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <DollarSign className="w-6 h-6 text-emerald-600" />
            Select Project for Draw Request
          </DialogTitle>
          <DialogDescription>
            Choose a project to create a new bank draw request
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4">
          {projectsWithFunds.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No projects with available funds</p>
              <p className="text-slate-400 text-sm mt-1">
                Complete some work and mark phases as paid to request a draw
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {projectsWithFunds.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleSelectProject(project.id)}
                  className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group text-left"
                  data-testid={`select-project-${project.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {project.name}
                    </h3>
                    <p className="text-sm text-slate-500 truncate">{project.address}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <div className="text-right">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Available</p>
                      <p className="font-bold text-emerald-600 font-mono">
                        {formatCurrency(project.loan_available_for_draw)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {projectsWithFunds.length > 0 && (
          <div className="pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500 text-center">
              Total available across {projectsWithFunds.length} project{projectsWithFunds.length !== 1 ? 's' : ''}: {' '}
              <span className="font-semibold text-emerald-600">
                {formatCurrency(projectsWithFunds.reduce((sum, p) => sum + (p.loan_available_for_draw || 0), 0))}
              </span>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
